import React, { ReactElement, useCallback, useState } from 'react'
import { DepartmentSearchItem } from '@/services/shipper/types'
import useModal from '@/hooks/useModal'
import { useGetSignupDepartments } from '@/services/shipper/useShipperQueries'
import MAlert from '@/utils/modal/MAlert'
import { VStack, Modal } from '@musinsa/mwms-ds'
import styled from 'styled-components'

// *** 주의사항 ***
// trigger 은 onClick props를 받아서 처리 가능해야 합니다.

interface DepartmentSelectModalProps {
  trigger: React.ReactNode
  shipperCode: string // 고정
  shipperName: string // 고정
  onSelect: (params: DepartmentSearchItem) => void
}

function DepartmentSelectModal({
  trigger,
  shipperName,
  shipperCode,
  onSelect,
}: DepartmentSelectModalProps) {
  const { open, onOpenModal, onCloseModal } = useModal()
  const [selectedIdx, setSelectedIdx] = useState(-1)

  // Okta 회원가입 부서 검색 API(LOGISPRD-8746 /api/v1/auth/departments). enabled:false → refetch 시점에만 조회.
  const active = useGetSignupDepartments(shipperCode)
  const list = active.data?.list ?? []

  const handleOpen = useCallback(() => {
    setSelectedIdx(-1)
    active.refetch()
    onOpenModal()
  }, [active, onOpenModal])

  const handleClose = useCallback(() => {
    setSelectedIdx(-1)
    onCloseModal()
  }, [onCloseModal])

  const onSubmit = useCallback(() => {
    if (selectedIdx === -1) return MAlert.show('부서를 선택해주세요')
    onSelect(list[selectedIdx])
    handleClose()
  }, [selectedIdx, list, onSelect, handleClose])

  return (
    <>
      {trigger
        ? React.cloneElement(trigger as ReactElement, { onClick: handleOpen })
        : null}
      {open && (
        <Modal
          open
          size="sm"
          title="부서 찾기"
          closable={false}
          customFooter={
            <>
              <CancelBtn type="button" onClick={handleClose}>취소</CancelBtn>
              <ConfirmBtn type="button" disabled={selectedIdx === -1} onClick={onSubmit}>선택완료</ConfirmBtn>
            </>
          }
          onClose={handleClose}
        >
          <VStack gap={16}>
            {/* 선택된 소속 — 비활성(회색) 검색 필드 (Figma 39:51537). 입력 불가, 부서는 아래 목록에서 선택 */}
            <SearchWrap>
              <SearchSvg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </SearchSvg>
              <SearchInput value={shipperName} disabled readOnly />
            </SearchWrap>

            {/* 부서 테이블 */}
            <TableBorder>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <Th style={{ width: 48, textAlign: 'center' }} />
                    <Th>부서명</Th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr>
                      <td colSpan={2}>
                        <EmptyState>
                          <EmptyIconWrap>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                              <rect x="4" y="6" width="20" height="16" rx="2" stroke="#bdbdbd" strokeWidth="1.4" fill="none" />
                              <path d="M4 10h20" stroke="#bdbdbd" strokeWidth="1.4" />
                              <path d="M9 14h4M9 18h6" stroke="#bdbdbd" strokeWidth="1.2" strokeLinecap="round" />
                            </svg>
                          </EmptyIconWrap>
                          <EmptyText>부서 목록이 없습니다.</EmptyText>
                        </EmptyState>
                      </td>
                    </tr>
                  )}
                  {list.map((item, idx) => (
                    <ResultRow
                      key={item.DEPT_NM}
                      $selected={idx === selectedIdx}
                      onClick={() => setSelectedIdx(idx)}
                    >
                      <td style={{ textAlign: 'center', padding: '0', height: 36 }}>
                        <input
                          type="radio"
                          checked={idx === selectedIdx}
                          onChange={() => setSelectedIdx(idx)}
                          style={{ cursor: 'pointer', accentColor: '#245eff', width: 16, height: 16 }}
                        />
                      </td>
                      <td style={{ padding: '0 12px', fontSize: 14, color: '#000', height: 36 }}>{item.DEPT_NM}</td>
                    </ResultRow>
                  ))}
                </tbody>
              </table>
            </TableBorder>
          </VStack>
        </Modal>
      )}
    </>
  )
}

/* ── Styled Components (Figma 39:51537) ── */

const FooterBtn = styled.button`
  min-width: 56px;
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
`

const CancelBtn = styled(FooterBtn)`
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #000;

  &:hover {
    background: #f7f7f7;
  }
`

const ConfirmBtn = styled(FooterBtn)`
  background: #000;
  border: 1px solid #000;
  color: #fff;

  &:not(:disabled):hover {
    background: #222;
  }

  &:disabled {
    background: rgba(138, 138, 138, 0.1);
    border-color: transparent;
    color: rgba(0, 0, 0, 0.2);
    cursor: not-allowed;
  }
`

const SearchWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SearchSvg = styled.svg`
  position: absolute;
  left: 12px;
  width: 20px;
  height: 20px;
  color: #adb5bd;
  pointer-events: none;
  flex-shrink: 0;
`

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 16px 0 40px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  background: #f5f5f5;
  color: #999;
  cursor: default;
`

const TableBorder = styled.div`
  border: 1px solid #ebebeb;
  border-radius: 4px;
  overflow: hidden;
`

const Th = styled.th`
  padding: 0 12px;
  height: 36px;
  border-bottom: 1px solid #ebebeb;
  font-weight: 500;
  font-size: 14px;
  text-align: left;
  color: #666;
  background: #f8f9fc;
`

const EmptyState = styled.div`
  padding: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`

const EmptyIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`

const EmptyText = styled.span`
  font-size: 14px;
  color: #888;
  line-height: 1.4;
`

const ResultRow = styled.tr<{ $selected: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#f5f7ff' : 'transparent')};
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $selected }) => ($selected ? '#f5f7ff' : '#fafafa')};
  }
`

export default DepartmentSelectModal
